import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  ImageEditor,
  ImageEditorProps,
} from '@synoptic/ui-kit/image-editor/image-editor.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { VisuallyHidden } from '@synoptic/ui-kit/visually-hidden/visually-hidden.js';
import {
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { S3ObjectInput } from '@/__generated__/graphql.ts';
import { useUserUploads } from '../s3/user/use-uploads.ts';
import { waitForUploads } from '../s3/wait-for-uploads.ts';
import { ImageUploadPreview } from './image-upload-preview.tsx';
import {
  formInfo,
  formMainText,
  formSubText,
  imageContainer,
  updateImageForm,
} from './update-image-modal.css.ts';

export interface UpdateImageFormProps extends ComponentPropsWithoutRef<'form'> {
  onUpdate: (image: S3ObjectInput) => void;
  mainTextEmpty: string;
  mainText: string;
  subTextEmpty: string;
  subText: string;
  editorProps: Omit<ImageEditorProps, 'image' | 'onSubmit'>;
  previewBorderRadius?: number;
  renderPreview: (previewSrc?: string) => ReactNode;
}

export const UpdateImageForm: FC<UpdateImageFormProps> = ({
  onUpdate,
  subText,
  mainText,
  mainTextEmpty,
  subTextEmpty,
  editorProps,
  renderPreview,
  previewBorderRadius,
  className,
}) => {
  const { handleSubmit, formState } = useForm();
  const toast = useToast();

  const {
    uploadFileList,
    uploads,
    onRemove: onRemoveUpload,
  } = useUserUploads();
  const [originalFile, setOriginalFile] = useState<File>();

  const [isDragOver, setIsDragOver] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const submit = async () => {
    let image: S3ObjectInput;
    try {
      [image] = await waitForUploads(uploads);
    } catch {
      toast.error({
        title: 'Your media failed to upload.',
      });
      return;
    }

    onUpdate(image);
  };

  return !originalFile ? (
    <form
      ref={formRef}
      className={updateImageForm}
      onSubmit={handleSubmit(submit)}
      data-drag-over={isDragOver ? '' : undefined}
      onDragOver={(e) => {
        if (e.dataTransfer.items.length > 0) {
          e.preventDefault();
          setIsDragOver(true);
        }
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        if (e.dataTransfer.files.length > 0) {
          if (uploads.length < 1) {
            e.preventDefault();
            setOriginalFile(e.dataTransfer.files[0]);
          }
          setIsDragOver(false);
        }
      }}
      onPaste={(e) => {
        if (e.clipboardData.files.length > 0 && uploads.length < 1) {
          e.preventDefault();
          setOriginalFile(e.clipboardData.files[0]);
        }
      }}
    >
      <div className={imageContainer}>
        <div className={className}>
          {uploads.length > 0 ? (
            <ImageUploadPreview
              borderRadius={previewBorderRadius}
              key={uploads[0].key}
              file={uploads[0].file}
              state={uploads[0].state}
              progress={uploads[0].progress}
              onRemove={() => onRemoveUpload(uploads[0].key)}
              setFile={setOriginalFile}
              renderPreview={renderPreview}
            />
          ) : (
            renderPreview()
          )}
        </div>
      </div>

      <div className={formInfo}>
        <span className={formMainText}>
          {uploads.length === 0 ? mainTextEmpty : mainText}
        </span>
        <span className={formSubText}>
          {uploads.length === 0 ? subTextEmpty : subText}
        </span>
      </div>

      {uploads.length === 0 ? (
        <Button asChild>
          <label>
            Add photo
            <VisuallyHidden>
              <input
                onChange={(e) => {
                  if (e.target.files) {
                    setOriginalFile(e.target.files[0]);
                  }
                }}
                type="file"
                accept="image/*"
              />
            </VisuallyHidden>
          </label>
        </Button>
      ) : (
        <Button loading={formState.isSubmitting} type="submit">
          Update photo
        </Button>
      )}
    </form>
  ) : (
    <ImageEditor
      image={originalFile}
      onSubmit={(file) => {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        uploadFileList(dataTransfer.files);
        setOriginalFile(undefined);
      }}
      {...editorProps}
    />
  );
};
